// Client-side form checks (FR-005, FR-039, FR-043, FR-049; data-model §9; research R12).
// Each check returns the value to send or a field code; messages.ts turns codes into copy, the same
// way it turns the server's field errors into copy. The server stays the authority.

export type FieldCode =
  "REQUIRED" | "INVALID_FORMAT" | "TOO_SHORT" | "TOO_LONG" | "MISMATCH";

export type Check<T = string> =
  { ok: true; value: T } | { ok: false; code: FieldCode };

const ok = <T>(value: T): Check<T> => ({ ok: true, value });
const fail = (code: FieldCode): Check<never> => ({ ok: false, code });

const CONTROL = /[\u0000-\u001f\u007f]/;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INDIAN_MOBILE = /^[6-9]\d{9}$/;

export const NAME_MAX = 100;
export const EMAIL_MAX = 254;
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

/** Name: trimmed, 1–100 characters, no control characters. */
export function checkName(raw: string): Check {
  const value = raw.trim();
  if (value === "") return fail("REQUIRED");
  if (value.length > NAME_MAX) return fail("TOO_LONG");
  if (CONTROL.test(value)) return fail("INVALID_FORMAT");
  return ok(value);
}

/** Email: trimmed (case kept), ≤ 254, one @ with a local part and a dotted domain, no spaces. */
export function checkEmail(raw: string): Check {
  const value = raw.trim();
  if (value === "") return fail("REQUIRED");
  if (value.length > EMAIL_MAX) return fail("TOO_LONG");
  if (!EMAIL_SHAPE.test(value)) return fail("INVALID_FORMAT");
  return ok(value);
}

/** A new password: 8–128 UTF-16 code units (Java String.length()), no composition rules. */
export function checkNewPassword(password: string): Check {
  if (password === "") return fail("REQUIRED");
  if (password.length < PASSWORD_MIN) return fail("TOO_SHORT");
  if (password.length > PASSWORD_MAX) return fail("TOO_LONG");
  return ok(password);
}

/** The log-in password: 1–128, never trimmed. */
export function checkLoginPassword(password: string): Check {
  if (password === "") return fail("REQUIRED");
  if (password.length > PASSWORD_MAX) return fail("TOO_LONG");
  return ok(password);
}

/** The confirmation must equal the password exactly. */
export function checkConfirmation(
  password: string,
  confirmation: string,
): Check {
  if (confirmation === "") return fail("REQUIRED");
  return confirmation === password ? ok(confirmation) : fail("MISMATCH");
}

/**
 * The 10-digit Indian mobile number, or null. Spaces, hyphens and brackets are removed, then a
 * leading +91, a leading 91 (12 digits) or a leading 0 (11 digits).
 */
export function normaliseMobile(raw: string): string | null {
  let digits = raw.replace(/[\s\-()]/g, "");
  if (digits.startsWith("+91")) digits = digits.slice(3);
  else if (digits.length === 12 && digits.startsWith("91"))
    digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith("0"))
    digits = digits.slice(1);
  return INDIAN_MOBILE.test(digits) ? digits : null;
}

export function checkMobile(raw: string): Check {
  if (raw.trim() === "") return fail("REQUIRED");
  const value = normaliseMobile(raw);
  return value === null ? fail("INVALID_FORMAT") : ok(value);
}
